import { ResolveFn, Router } from '@angular/router';
import { inject } from '@angular/core';

import { Store, select } from '@ngrx/store';

import { Observable, of } from 'rxjs';
import { take, map, filter, catchError, switchMap } from 'rxjs/operators';

import * as actions from '../actions';
import * as fromRoot from '../reducers';

import { CONSTANTS } from '../constants';
import { PersonalInfo, Guest } from '../models';
import { GuestDetailsService } from '../modules/core/services/guest-details.service';
import {TravelDocumentsService} from '../modules/core/services/travel-documents.service';

export const personalDetailResolver: ResolveFn<any> = (): Observable<PersonalInfo> | any => {
  const guestDetailsService: GuestDetailsService = inject(GuestDetailsService);
  const travelDocumentsService: TravelDocumentsService = inject(TravelDocumentsService);
	const store: Store<fromRoot.AppState> = inject(Store<fromRoot.AppState>);
  const router: Router = inject(Router);
  const constants: any = inject(CONSTANTS);

  return store.pipe(
    select(fromRoot.getSessionState),
    filter(resData => resData.reservationRetrievalResp !== null || !!resData.error),
    take(1),
    switchMap(resData => {
      if (resData.error) {
        router.navigate([constants.ROUTES.ERROR]);
        return of(null);
      }

      const guestId = getAlreadySelectedGuestId(store);
      const guests = getSelectedGuestsForCheckin(store);

      if (!guests?.length) {
        router.navigate([constants.ROUTES.SELECT_GUESTS_FOR_CHECKIN]);
        return of(null);
      }

      guestDetailsService
        .getGuestsDetails(guests)
        .pipe(take(1))
        .subscribe(beginDetailsResponse => {
          if (beginDetailsResponse && beginDetailsResponse.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
            return store.dispatch(new actions.LoadBeginDetailsSuccessAction(beginDetailsResponse));
          }

          return travelDocumentsService
            .getTravelDocuments(beginDetailsResponse)
            .pipe(take(1))
            .subscribe(travelDocuments => {

              const {
                emptyTravelDocuments: documentTypes
              } = travelDocumentsService.retrieveEmptyTravelDocuments(travelDocuments);

              const beginDetailsResponseUpdated = guestDetailsService.updatedBeginDetailsResponse(
                beginDetailsResponse,
                documentTypes
              );
              store.dispatch(new actions.LoadBeginDetailsSuccessAction(beginDetailsResponseUpdated));

              return travelDocuments;
            });
        });

      return guestDetailsService.loaded().pipe(
        take(1),
        map(() => {
          if (guestDetailsService.error) {
            throw guestDetailsService.error;
          }

          const currentGuestId = setCurrentGuestId(guests, guestId);
          store.dispatch(new actions.SetCurrentGuestIdAction(currentGuestId));
          store.dispatch(new actions.UpdateSelectedGuestAction(currentGuestId));
          return true;
        }),
        catchError(() => {
          router.navigate([constants.ROUTES.ERROR]);
          return of(null);
        })
      );
    })
  );
}

const setCurrentGuestId = (guests: Guest[], guestId: number | null): number | null => {
  const firstGuestSelected = guests?.[0]?.guestId ?? null;
  return guestId ?? firstGuestSelected;
}

const getAlreadySelectedGuestId = (store: Store<fromRoot.AppState>): number | null =>{
  let guestId = null;
  store.pipe(select(fromRoot.getEntitySelectedGuestId), take(1)).subscribe(res => {
    guestId = res;
  });
  if (!guestId) {
    store.pipe(select(fromRoot.getEntitySelectedOther), take(1)).subscribe(res => {
      guestId = res ? res.guestId : null;
    });
  }
  return guestId;
}

const getSelectedGuestsForCheckin = (store: Store<fromRoot.AppState>): Guest[] => {
  let guests:any = [];
  store.pipe(select(fromRoot.getSelectdGuestsForCheckinProcess), take(1)).subscribe(res => {
    guests = res;
  });
  return guests;
}
